import { Box, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { type BasicTableColumn, type BasicTableRow } from './BasicTable'
import { Delete, Edit } from '@mui/icons-material'
import type React from 'react'
import { LockedActivityActionTooltip } from '../LockedActivityActionTooltip'

export interface SubBasicTableColumn extends BasicTableColumn {
  isTrackingColumn?: boolean
}

export interface SubBasicTableRow extends BasicTableRow {
  isTrackingRow?: boolean
}

interface Props {
  hasTrackingDetails?: boolean
  columns: SubBasicTableColumn[]
  rows: SubBasicTableRow[]
  onEditAdditionalDetails?: (row: any) => void
  onDeleteAdditionalDetails?: (row: any) => void
  onEditTracking?: (row: any) => void
  onDeleteTracking?: (row: any) => void
  hideDelete?: boolean
  dataTestId?: string
  readonly?: boolean
  trackingReadonly?: boolean
}
export const SubBasicTable = (props: Props): JSX.Element => {
  const allowUpdates = props.readonly !== true
  const allowTrackingUpdates = props.trackingReadonly !== true
  const showActionButtons = allowUpdates || allowTrackingUpdates
  const shouldShowTrackingColumns = props.hasTrackingDetails === true

  const getColumnHeaderStyle = (column: SubBasicTableColumn): React.CSSProperties => {
    const style: React.CSSProperties = { minWidth: column.minWidth }
    if (column.isTrackingColumn === true && shouldShowTrackingColumns) {
      style.backgroundColor = 'var(--grey-500)'
    } else {
      style.backgroundColor = 'rgba(217, 217, 217, 1)'
    }
    return style
  }

  const renderActionButtons = (row: SubBasicTableRow, onEdit?: (row: SubBasicTableRow) => void, onDelete?: (row: SubBasicTableRow) => void, dataTestId?: string): JSX.Element | null => {
    if (!showActionButtons) return null
    return (
      <LockedActivityActionTooltip lock={row.activityLock}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size='small' onClick={() => { onEdit?.(row) }} data-testid={dataTestId != null ? `${dataTestId}-editButton` : 'editButton'}>
            <Edit fontSize='inherit' />
          </IconButton>
          {props.hideDelete !== true && (
            <IconButton size='small' onClick={() => { onDelete?.(row) }} data-testid={dataTestId != null ? `${dataTestId}-deleteButton` : 'deleteButton'}>
              <Delete fontSize='inherit' />
            </IconButton>
          )}
        </Box>
      </LockedActivityActionTooltip>
    )
  }

  return (
    <TableContainer
      data-testid={props.dataTestId ?? 'subBasicTable'}
      sx={{ overflow: 'auto', borderRadius: '8px', border: '1px solid rgba(204, 204, 204, 1)' }}
    >
      <Table style={{ breakInside: 'avoid' }} size='small'>
        <TableHead>
          <TableRow>
            {props.columns.map((column) => {
              return (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={getColumnHeaderStyle(column)}
                >
                  <Typography
                    variant='body1'
                    fontWeight={600}
                    color={(shouldShowTrackingColumns && column.isTrackingColumn === true) ? 'var(--white)' : undefined}
                  >
                    {column.isTrackingColumn === true && !shouldShowTrackingColumns
                      ? ''
                      : column.label}
                  </Typography>
                </TableCell>
              )
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {props.rows.map((row) => {
            return (
              <TableRow
                key={row.id}
                data-testid='subBasicTableRow'
                sx={{ backgroundColor: 'var(--white)' }}
              >
                {props.columns.map((column) => {
                  if (column.id === 'action' && allowUpdates) {
                    return <TableCell key={column.id} sx={{ borderRight: shouldShowTrackingColumns ? '2px solid rgba(204, 204, 204, 1)' : 'none' }}>
                      {renderActionButtons(row, props.onEditAdditionalDetails, props.onDeleteAdditionalDetails, 'additionalDetails')}
                    </TableCell>
                  }
                  if (column.id === 'trackingAction' && allowTrackingUpdates) {
                    return <TableCell key={column.id}>
                      {shouldShowTrackingColumns && renderActionButtons(row, props.onEditTracking, props.onDeleteTracking, 'tracking')}
                    </TableCell>
                  }

                  return (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        maxWidth: column.minWidth,
                        alignContent: 'center'
                      }}
                      data-testid={`subBasicTableCell${column.id}`}
                    >
                      {(column.isTrackingColumn === true && !shouldShowTrackingColumns)
                        ? ''
                        : row[column.id]}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
